import { QICon_caf_pause_button, QICon_caf_play_button, QIcon_arrow_left } from '@repo/icons/index'
import { LegacyRef, useEffect, useRef, useState } from 'react'
import { HIGHLIGHT_TAG_MAP } from '../../../../../model/caf.interface'
import { useCafHighlightV5 } from '../../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'

// import HighlightDetailListAI from '../../../../webview/caf/highlight/HighlightDetailListAI'

import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useAudioPlayer from '../../../../../modules/hooks/webview/caf/etc/useAudioPlayer'
import useRNMsg, { ringleAppNavs } from '../../../../../modules/hooks/webview/common/useRNMsg'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import { ICON_ALARM_GRAY, ICON_PAUSE_BUTTON_GREY_BG, ICON_PLAY_BUTTON } from '../../../../../modules/svg/svg'
import { Bar, PlayerPropsType } from '../../common/AudioController'
import { useCafSidePopup } from '../../common/CafSidePopup'
import { getErrorMarked } from '../../highlight/HighlightDetailListPronunciation'
import ErrorReportPopupComponent from './CafErrorReportPopup'
import SidePopupBody from './components/Body'

export interface SidePopupBodyProps {
  headerProps: SidePopupHeaderProps
  children: JSX.Element | JSX.Element[] | string | string[]
}
export interface SidePopupHeaderProps {
  title: string | JSX.Element
  onClose: () => void
  SubIcon?: any
  onClickSubIcon?: any
}

const DEFAULT_PROPS = {
  hFull: true,
  bgWhite: true,
  maxW: 512,
  noTopSide: true,
  overlayClose: true,
}
const useHighlightDetailSidePopup = (backBtn, onClosePopup = () => null) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { sendPostMsg } = useRNMsg()
  const { tab, tag, highlightId, lessonId, errorId, back } = router.query
  const sidePopup = useCafSidePopup()
  const [showPopup, setShowPopup] = useState(false)
  const isMobile = useIsMobile()
  const { data, error, refetch, isLoading } = useCafHighlightV5(+highlightId)

  const tag_num = Number(tag)
  const highlightId_num = Number(highlightId)

  const highlightDetail = {
    ...data?.pages[0],
    details: data?.pages?.reduce((ac, cr) => {
      return [...ac, ...cr.details]
    }, []),
  }
  const handleClickErrorReport = (contentId, contentSentence) => () => {
    if (isMobile) {
      sendPostMsg({
        nav: ringleAppNavs.cafErrorReport,
        detail_id: contentId,
        detail_content: contentSentence,
        from: 'highlight',
      })
    } else {
      setShowPopup(true)
    }
  }

  const goBack = () => {
    router.push(urls.student.newPortal.lessons.reviewDetail.asCafTagAndIdBack(lessonId, tab, tag, highlightId))
  }
  useEffect(() => {
    if (highlightId_num >= 0 && !isLoading && +errorId >= 0) {
      const popupId = `caf_highlight_detail_${tag_num}`
      const selectedSentence = highlightDetail?.details.find((detail) => Number(detail.id) === Number(errorId))

      const onClose = () => {
        sidePopup.close(popupId)
        onClosePopup && onClosePopup()
      }

      sidePopup.show({
        ...DEFAULT_PROPS,
        id: popupId,
        onClose: onClose,
        body: (
          <SidePopupBody
            headerProps={{
              title: (
                <div className="flex flex-row items-center justify-start text-[20px] font-bold text-black">
                  {backBtn && (
                    <QIcon_arrow_left width={24} height={24} className="mr-[8px] cursor-pointer" onClick={goBack} />
                  )}
                </div>
              ),
              onClose: onClose,
            }}
          >
            <div className="flex w-[100%] flex-col items-start justify-start pr-[56px]">
              {+tag === HIGHLIGHT_TAG_MAP.pronunciation && selectedSentence && (
                <PronunciationHighlightDetail
                  detail={selectedSentence}
                  contentId={errorId}
                  handleClickErrorReport={handleClickErrorReport}
                  showPopup={showPopup}
                  setShowPopup={setShowPopup}
                />
              )}
            </div>
          </SidePopupBody>
        ),
        bgColor: COLORS.GRAY50,
      })
    }
  }, [tag_num, highlightId_num, highlightDetail, isLoading, errorId, back])
}

export const PronunciationHighlightDetail = ({
  detail,
  contentId,
  handleClickErrorReport,
  showPopup,
  setShowPopup,
}) => {
  const { t } = useTranslation()
  const audioId = `audio_${detail.audio_url}`
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const play = () => {
    audioRef?.current.play()
  }
  const pause = () => {
    audioRef?.current.pause()
  }
  if (!detail) return null
  const syllableString = detail?.syllables.join('•')
  return (
    <div>
      <div className="border-1 flex w-full flex-col rounded-[8px] border-gray-200 bg-white p-[16px]">
        <div className="border-b-1 border-gray-200 pb-[12px] text-[18px] font-bold text-gray-700">
          {detail.sentence}
        </div>
        <div className=" flex flex-row items-center justify-start border-gray-200 pt-[12px] text-[18px] font-bold text-gray-700">
          {!isPlaying ? (
            <QICon_caf_play_button width={32} height={32} className="mr-[8px] cursor-pointer" onClick={play} />
          ) : (
            <QICon_caf_pause_button width={32} height={32} className="mr-[8px] cursor-pointer" onClick={pause} />
          )}
          <audio
            src={detail.audio_url}
            id={audioId}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            ref={audioRef}
            className="hidden"
          />
          <div className="text-[18px] font-normal text-gray-500">{syllableString}</div>
        </div>
      </div>
      <div>
        <div className="mt-[24px] flex flex-col items-start justify-start">
          <div className="mb-[12px] text-[14px] font-normal text-gray-700">
            {t(n_caf.complexity.subsection_info2(detail.sentences.length))}
          </div>
          {detail?.sentences.map((sentence, i) => (
            <PronunciationDetailItem
              sentence={sentence}
              // setIsPlaying={setIsPlaying}
              syllables={detail.syllables}
              key={`${i}`}
              index={i}
              contentId={contentId}
              handleClickErrorReport={handleClickErrorReport}
              showPopup={showPopup}
              setShowPopup={setShowPopup}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const PronunciationDetailItem = ({
  sentence,
  syllables,
  index,
  contentId,
  handleClickErrorReport,
  showPopup,
  setShowPopup,
}) => {
  const { t } = useTranslation()
  const replacedTextWrapperRef = useRef(null)
  const audioRef: LegacyRef<HTMLAudioElement> = useRef()
  useEffect(() => {
    const targetText = sentence?.sentence
    const errorIndexs = sentence.sentence_error_index

    if (targetText && errorIndexs && replacedTextWrapperRef.current) {
      const applied = getErrorMarked(targetText, errorIndexs)
      replacedTextWrapperRef.current.innerHTML = applied
    }
  }, [sentence, replacedTextWrapperRef.current])

  if (!sentence) return null
  return (
    <div className="border-1 mb-[16px] flex w-full flex-col items-start justify-start rounded-[8px] border-gray-200 bg-white p-[16px]">
      <div className="text-[14px] font-bold text-gray-700">
        {t(n_caf.pronunciation.area_sentence)} {` ${index + 1}`}
      </div>
      <div
        className="mt-[12px] rounded-[8px] bg-gray-50 py-[8px] px-[16px] text-[16px] text-gray-700"
        ref={replacedTextWrapperRef}
      ></div>
      {sentence?.audio_url && (
        <div className="my-[12px] w-full rounded-[8px]">
          <AudioController src={sentence?.audio_url || ''} id={sentence?.audio_url} audioRef={audioRef} />
        </div>
      )}
      <ErrorList content={sentence} syllables={syllables} />
      <div
        className="mt-[24px] flex h-[25px] w-full cursor-pointer items-center text-[12px] font-medium tracking-wide text-gray-500 sm:text-[14px]"
        onClick={handleClickErrorReport(contentId, sentence)}
      >
        <img src={ICON_ALARM_GRAY} alt="alarm" className="mb-4" />
        <div className="ml-2 mt-1">{t(n_caf.highlight.report)}</div>
        <ErrorReportPopupComponent
          content={sentence?.sentence || ''}
          type="highlight"
          isShowEditPopup={showPopup}
          setShowEditPopup={setShowPopup}
          id={contentId}
        />
      </div>
      <ErrorReportPopupComponent
        content={sentence?.sentence || ''}
        type="highlight"
        isShowEditPopup={showPopup}
        setShowEditPopup={setShowPopup}
        id={contentId}
      />
    </div>
  )
}

export const ErrorList = ({ content, syllables }) => {
  if (!content) return
  const errors = content?.word_error_index || []
  return (
    <div className="mt-[8px] flex flex-col items-start justify-center px-[16px]">
      {errors.map((error, i) => {
        return <ErrorItem syllables={syllables} error={error} key={`${error.sentence}_${i}`} />
      })}
    </div>
  )
}
const ErrorItem = ({
  syllables,
  error,
}: {
  syllables: string[]
  error: { right: string; wrong: string; index: number }
}) => {
  const { t } = useTranslation()
  const errorTargetString = syllables[error.index]

  return (
    <div className="flex flex-row items-center justify-start">
      <div>
        {syllables.map((syllable, i) => (
          <span key={`${syllable}_${i}`}>
            {i !== 0 && '•'}
            {i === error.index ? (
              <span className="text-[14px] text-red-400">{syllable}</span>
            ) : (
              <span className="text-[14px] text-gray-500">{syllable}</span>
            )}
          </span>
        ))}
      </div>
      <div className="mx-[16px] h-[20px] w-[1px] bg-gray-200"></div>
      <div className="text-[14px] text-gray-500">{t(n_caf.pronunciation.error(error.right, error.wrong))}</div>
    </div>
  )
}

const AudioController = ({ src, id, onPlay, onPause, audioRef }: PlayerPropsType) => {
  const audioId = `audio_${id}`
  const [isReady, setIsReady] = useState(false)
  const { curTime, playing, setPlaying, setClickedTime, pauseAudio, duration } = useAudioPlayer({
    audioId: audioId,
    isReady,
  })

  const onCanPlayAudio = () => {
    setIsReady(true)
  }
  return (
    <>
      <div className="w-full rounded-sm bg-gray-50 p-[12px] pb-[8px] pr-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex-0 pr-[12px] pt-[4px]">
            {/* Pause & play button */}
            {playing ? (
              <button onClick={() => setPlaying(false)} className="focus:outline-none">
                <img src={ICON_PAUSE_BUTTON_GREY_BG} width={28} height={28} className="max-w-none" alt="pause" />
              </button>
            ) : (
              <button onClick={() => setPlaying(true)} className="focus:outline-none">
                <img src={ICON_PLAY_BUTTON} width={28} height={28} className="max-w-none" alt="play" />
              </button>
            )}
          </div>
          <div className="flex flex-1 translate-x-[-5px] items-center justify-center">
            <Bar curTime={curTime} duration={duration} onTimeUpdate={(time) => setClickedTime(time)} />
          </div>
          {src && (
            <audio
              src={src}
              id={audioId}
              onEnded={() => setPlaying(false)}
              onCanPlay={onCanPlayAudio}
              onPlay={() => {
                onPlay && onPlay()
              }}
              onPause={() => {
                onPause && onPause()
              }}
              ref={audioRef}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default useHighlightDetailSidePopup
